<template>
  <div class="flex">
    <div
      class="fixed top-30 z-40 w-88 flex-shrink-0 border-r border-slate-400 bg-pure-white"
    >
      <div class="p-6 border-b border-slate-400 flex flex-col gap-3">
        <div>
          <div class="mb-1"><BlTextBody2 text="Availabilities" /></div>
          <BlTextTitle3 text="Therapists" />
        </div>
      </div>
      <div
        v-if="pageLoading"
        class="w-full grid place-items-center"
        :style="{ height: 'calc(100vh - 15rem)' }"
      ></div>
      <div
        v-else
        class="overflow-y-auto"
        :style="{ height: 'calc(100vh - 15rem)' }"
      >
        <div id="active-section" class="py-1 px-3 border-b border-slate-400">
          <div class="flex items-center justify-between px-3 py-2 mb-1">
            <BlTextSubtitle3 text="Active therapists" class="text-slate-600" />
            <BlBubbleCount :count="activeTherapists.length || 0" />
          </div>
          <div class="flex flex-col gap-1">
            <div
              v-for="therapist in activeTherapists"
              :key="therapist.id"
              class="h-11 px-3 flex items-center rounded hover:bg-hover-state cursor-pointer"
              :class="{ 'bg-slate-300': selectedTherapistId === therapist.id }"
              @click="onSelectTherapist(therapist.id)"
            >
              <div class="truncate" :title="therapist.name">
                <BlTextSubtitle3 :text="therapist.name" />
              </div>
            </div>
          </div>
        </div>
        <div id="deactive-section" class="py-1 px-3">
          <div
            class="flex items-center justify-between px-3 py-2 mb-1 cursor-pointer"
            @click="deactivatedTherapistsOpened = !deactivatedTherapistsOpened"
          >
            <BlTextSubtitle3
              text="Deactive therapists"
              class="text-slate-600"
            />
            <div class="flex items-center gap-2">
              <BlBubbleCount :count="deactivatedTherapists.length || 0" />
              <BlIcon
                icon="chevron-up"
                class="w-4 h-4 transition-all duration-300"
                :style="
                  deactivatedTherapistsOpened
                    ? { rotate: '0deg' }
                    : { rotate: '180deg' }
                "
              />
            </div>
          </div>
          <div v-if="deactivatedTherapistsOpened" class="flex flex-col gap-1">
            <div
              v-for="therapist in deactivatedTherapists"
              :key="therapist.id"
              class="h-11 px-3 flex items-center rounded hover:bg-hover-state cursor-pointer"
              :class="{ 'bg-slate-300': selectedTherapistId === therapist.id }"
              @click="onSelectTherapist(therapist.id)"
            >
              <div class="truncate" :title="therapist.name">
                <BlTextSubtitle3 :text="therapist.name" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="pl-88">
      <div
        class="h-12 px-6 sticky top-30 z-30 bg-slate-300 border-b border-slate-400 flex items-center justify-between gap-4"
        :style="{ minWidth: 'calc(100vw - 22rem - 0.5rem)' }"
      >
        <div class="sticky top-30 left-94">
          <BlTextBody2 :text="masterDate" />
        </div>
        <div class="sticky top-30 right-6 flex items-center gap-2">
          <div>
            <BlSelectBox
              v-model="timeslotView"
              :options="timeslotViewOptions"
              dense
              @change="$emit('set-timeslot-view', $event)"
            />
          </div>
          <BlInputButton
            type="outlined"
            dense
            label="Jump to"
            @clicked="jumpToOpened = true"
          />
          <BlInputButton
            type="outlined"
            dense
            label="Today"
            @clicked="onDayClick({ date: new Date() })"
          />
          <BlInputButton
            type="outlined"
            dense
            class="rounded-full"
            icon="chevron-left"
            @clicked="onNextClick(-1)"
          />
          <BlInputButton
            type="outlined"
            dense
            class="rounded-full"
            icon="chevron-right"
            @clicked="onNextClick(1)"
          />
        </div>
      </div>
      <Timeslots
        :isCompact="timeslotView === 'compact'"
        :selectedTherapist="selectedTherapist"
        :selectedRoom="null"
        :selectedDate="date"
        :headers="timeslotHeaders"
        :loading="previewLoading"
        @after-commit="fetchAvailabilities"
      />
    </div>
    <JumpToDateModal
      v-if="jumpToOpened"
      :currentDate="date"
      dimension="weekly"
      @close-modal="jumpToOpened = false"
      @submit="onDayClick({ date: $event })"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

import JumpToDateModal from "./timeslot/JumpToDateModal.vue";
import Timeslots from "./timeslot/Timeslots.vue";

import BlTextTitle3 from "../BlTextTitle3.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlInputButton from "../BlInputButton.vue";
import BlSelectBox from "../BlSelectBox.vue";
import BlBubbleCount from "../BlBubbleCount.vue";
import BlIcon from "../BlIcon.vue";

export default {
  name: "AvailabilitiesTherapist",
  components: {
    JumpToDateModal,
    Timeslots,
    BlTextTitle3,
    BlTextSubtitle3,
    BlTextBody2,
    BlInputButton,
    BlSelectBox,
    BlBubbleCount,
    BlIcon
  },
  props: {
    timeslotView: { type: String },
    timeslotViewOptions: { type: Array }
    // @set-timeslot-view
  },
  data() {
    return {
      pageLoading: true,
      previewLoading: false,
      date: new Date(),
      selectedTherapistId: 0,
      activeTherapists: [],
      deactivatedTherapists: [],
      deactivatedTherapistsOpened: false,
      jumpToOpened: false
    };
  },
  computed: {
    ...mapGetters(["currentUser", "availabilities"]),
    selectedTherapist() {
      if (!this.selectedTherapistId) return null;
      const arr = [...this.activeTherapists, ...this.deactivatedTherapists];
      return arr.find(i => i.id === this.selectedTherapistId);
    },
    getParams() {
      let d = moment(this.date)
        .startOf("isoWeek")
        .format("YYYY-MM-DD");
      let params = `?date=${d}&type=weekly`;
      if (this.selectedTherapistId) {
        params += `&user_id=${this.selectedTherapistId}`;
      }
      return params;
    },
    masterDate() {
      const start = moment(this.date)
        .startOf("isoWeek")
        .format("ddd, DD MMM YYYY");
      const end = moment(this.date)
        .endOf("isoWeek")
        .format("ddd, DD MMM YYYY");
      return `${start} — ${end}`;
    },
    timeslotHeaders() {
      const startDate = moment(this.date)
        .startOf("isoWeek")
        .format("YYYY-MM-DD");
      const endDate = moment(this.date)
        .endOf("isoWeek")
        .format("YYYY-MM-DD");

      let current = moment(startDate);

      const dates = [];

      while (current.isSameOrBefore(endDate)) {
        const isSame =
          current.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD");
        dates.push({
          highlight: isSame,
          value: current.format("YYYY-MM-DD"),
          label: current.format("ddd DD")
        });
        current.add(1, "days");
      }

      return dates;
    }
  },
  watch: {},
  async mounted() {
    const isValidDate = d => {
      return d instanceof Date && !isNaN(d);
    };

    const searchParams = this.$route.query;
    if (searchParams["date"]) {
      const d = new Date(`${searchParams["date"]}T00:00:00`);
      if (isValidDate(d)) this.date = d;
    }
    if (searchParams["user_id"]) {
      const n = Number(searchParams["user_id"]);
      if (Number(n)) this.selectedTherapistId = Number(n);
    }

    this.pageLoading = true;
    this.previewLoading = true;

    await this.getRooms();
    this.fetchAllTherapist().then(() => {
      this.fetchAvailabilities();
    });
  },
  methods: {
    ...mapActions(["getUsers", "getRooms", "getAvailabilities"]),
    async fetchAllTherapist() {
      return this.getUsers({
        search: true,
        params: "?sort=name_asc&status=active,deactivated"
      }).then(({ success, data }) => {
        if (!success) return;
        const active = data.filter(i => i.status === "active");
        const deactivated = data.filter(i => i.status === "deactivated");
        if (!this.selectedTherapistId) {
          let id = 0;
          if (active.length) id = active[0].id;
          else if (deactivated.length) id = deactivated[0].id;
          if (id) this.onSelectTherapist(id);
        }
        this.activeTherapists = active;
        this.deactivatedTherapists = deactivated;
      });
    },
    fetchAvailabilities() {
      if (this.$route.path !== "/availabilities/therapist") return;

      this.previewLoading = true;
      this.getAvailabilities({ params: this.getParams }).then(async () => {
        this.previewLoading = false;
        this.pageLoading = false;
      });

      // sync url path for page
      const incomingPath = `/availabilities/therapist${this.getParams}`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    onDayClick(day) {
      const d = day.date;
      this.date = d;
      this.fetchAvailabilities();
    },
    onNextClick(num) {
      const change = 7;
      const d = new Date(this.date);
      if (num === 1) {
        this.onDayClick({ date: d.setDate(d.getDate() + change) });
      } else {
        this.onDayClick({ date: d.setDate(d.getDate() - change) });
      }
    },
    onSelectTherapist(id) {
      this.selectedTherapistId = Number(id);
      this.fetchAvailabilities();
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/availabilities/Therapist.vue